import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import InputCst from "@components/InputCst/InputCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import AutocompleteVirtualScrollCst from "@components/InputCst/AutocompleteVirtualScrollCst";
import { useFetchOptions } from "@hooks/useFetchOptions";
import { useGroupsList } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/hooks/useGroupsList";
import { RuleValueInputMeta } from "./useRuleDataset";

dayjs.extend(customParseFormat);

export const RULE_DATE_FORMAT = "DD/MM/YYYY";
export const RULE_MULTI_VALUE_SEPARATOR = ";";
const GROUPS_PAGE_SIZE = 20;

interface RuleOptionItem {
  value: string;
  label: string;
}

interface RuleValueBaseProps {
  id: string;
  label?: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const fullWidthSx = { minWidth: "0" };

const splitMultiValue = (value: string): string[] =>
  value ? value.split(RULE_MULTI_VALUE_SEPARATOR).filter(Boolean) : [];

const toRuleOption = (option: any): RuleOptionItem => ({
  value: String(option?.value ?? ""),
  label: String(option?.label ?? option?.value ?? ""),
});

const MultiValueInput: React.FC<
  RuleValueBaseProps & { optionsKey?: string; service?: string }
> = ({ id, label, value, disabled, onChange, optionsKey, service }) => {
  const { getOptions } = useFetchOptions(service, optionsKey);

  const options = useMemo<RuleOptionItem[]>(
    () => (getOptions() || []).map(toRuleOption),
    [getOptions],
  );

  const selected = useMemo<RuleOptionItem[]>(
    () =>
      splitMultiValue(value).map(
        (item) =>
          options.find((option) => option.value === item) ?? {
            value: item,
            label: item,
          },
      ),
    [value, options],
  );

  return (
    <MultiSelectCst
      id={id}
      label={label}
      value={selected}
      options={options}
      disabled={disabled}
      customWidth="100%"
      sx={fullWidthSx}
      fitTagsInOneLine
      onChange={(next: RuleOptionItem[]) =>
        onChange(
          next.map((option) => option.value).join(RULE_MULTI_VALUE_SEPARATOR),
        )
      }
    />
  );
};

const parseRuleDate = (value: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, RULE_DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

const DateValueInput: React.FC<RuleValueBaseProps> = ({
  id,
  label,
  value,
  disabled,
  onChange,
}) => {
  const parsed = useMemo(() => parseRuleDate(value), [value]);
  const [draft, setDraft] = useState<Dayjs | null>(parsed);

  useEffect(() => {
    setDraft(parsed);
  }, [parsed]);

  return (
    <DataPickerCst
      id={id}
      label={label}
      value={draft}
      disabled={disabled}
      customWidth="100%"
      sx={fullWidthSx}
      onChange={(next: Dayjs | null) => {
        setDraft(next);
        if (!next) {
          onChange("");
        } else if (next.isValid()) {
          onChange(next.format(RULE_DATE_FORMAT));
        }
      }}
    />
  );
};

const GroupsValueInput: React.FC<
  RuleValueBaseProps & { corporateId: string }
> = ({ id, label, value, disabled, onChange, corporateId }) => {
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(GROUPS_PAGE_SIZE);
  const knownLabels = useRef<Record<string, string>>({});

  const { items, totalItems, isLoading } = useGroupsList({
    corporateId,
    page: 1,
    pageSize,
    search,
    searchField: "name",
  });

  const options = useMemo<RuleOptionItem[]>(
    () =>
      items.map((group) => ({
        value: String(group.id),
        label: group.name || String(group.id),
      })),
    [items],
  );

  useEffect(() => {
    options.forEach((option) => {
      knownLabels.current[option.value] = option.label;
    });
  }, [options]);

  const selected = useMemo<RuleOptionItem | null>(() => {
    if (!value) return null;
    return (
      options.find((option) => option.value === value) ?? {
        value,
        label: knownLabels.current[value] ?? value,
      }
    );
  }, [value, options]);

  const handlePagination = (next?: string) => {
    if (next !== undefined) {
      setSearch(next);
      setPageSize(GROUPS_PAGE_SIZE);
      return;
    }
    if (!isLoading && pageSize < totalItems) {
      setPageSize((current) => current + GROUPS_PAGE_SIZE);
    }
  };

  return (
    <AutocompleteVirtualScrollCst
      id={id}
      label={label}
      value={selected}
      options={options}
      totalElements={totalItems}
      disabled={disabled}
      customWidth="100%"
      sx={fullWidthSx}
      footerVariant="compact"
      onChange={(next: RuleOptionItem | null) =>
        onChange(next ? next.value : "")
      }
      onChangePagination={handlePagination}
    />
  );
};

interface RuleValueInputProps extends RuleValueBaseProps {
  meta: RuleValueInputMeta;
  corporateId: string;
}

const RuleValueInput: React.FC<RuleValueInputProps> = ({
  meta,
  corporateId,
  ...props
}) => {
  switch (meta.kind) {
    case "multi":
      return (
        <MultiValueInput
          {...props}
          optionsKey={meta.optionsKey}
          service={meta.service}
        />
      );
    case "date":
      return <DateValueInput {...props} />;
    case "groups":
      return <GroupsValueInput {...props} corporateId={corporateId} />;
    default:
      return (
        <InputCst
          id={props.id}
          label={props.label}
          value={props.value}
          disabled={props.disabled}
          onChange={(e: any) => props.onChange(e.target.value)}
        />
      );
  }
};

export default RuleValueInput;
